import type { ReactNode } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

/**
 * 可勾选的选项行：左勾选、中标签、右明细。整行可点。
 *
 * 对齐搬小书 `apps/web/src/app/mobile/rows.tsx` 的 SelectRow —— 内容/类型选择
 * 一律用这种 list item，不用下拉框。
 */
export function SelectRow({
  checked,
  onCheckedChange,
  disabled,
  detail,
  footer,
  icon,
  action,
  children,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  detail?: ReactNode;
  /** 第二行（状态/说明），与标签左对齐 */
  footer?: ReactNode;
  /** 覆盖左侧勾选框（如备份进度用状态图标） */
  icon?: ReactNode;
  /** 右侧额外操作（在勾选行之外，点击不会切换勾选） */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'bg-muted/60 rounded-control px-3.5 py-3 transition-colors',
        'ios:rounded-none ios:bg-transparent ios:px-0 ios:py-3.5',
        disabled ? 'opacity-60' : 'active:bg-accent ios:active:bg-transparent ios:active:opacity-60',
      )}
    >
      <div className="flex w-full items-center gap-2">
        <label
          className={cn(
            'flex w-full items-center gap-3 text-left text-subheadline ios:text-body',
            !disabled && 'cursor-pointer',
          )}
        >
          {icon ?? (
            <Checkbox checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
          )}
          <span className="min-w-0 flex-1">{children}</span>
          {detail != null ? (
            <span className="text-muted-foreground shrink-0 text-footnote tabular-nums ios:text-body">
              {detail}
            </span>
          ) : null}
        </label>
        {action != null ? <div className="shrink-0">{action}</div> : null}
      </div>
      {footer != null ? (
        <div className="text-muted-foreground pl-7 text-caption1 tabular-nums ios:pl-[34px] ios:text-footnote">{footer}</div>
      ) : null}
    </div>
  );
}
